import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Button, Header, Image, Menu, MenuItemProps } from 'semantic-ui-react'
import close_modal from '../../../../assets/svg/close_modal.svg'
import withSuspense from '../../../../components/HOC/SuspenseHOC'
import TransitionModal from '../../../../components/Modal/TransitionModal'
import ValidationInput from '../../../../components/ValidationInput/ValidationInput'
import { useWindowSize } from '../../../../hooks/useWindowSize'
import { RootState } from '../../../../store/rootState'
import { IUserDetail } from '../../../../types/user'
import { checkRole, checkTeamMemberOrAdminStatus } from '../../../../utils/checkRole'
import { taskDisplayName } from '../../../../utils/formatters'
import * as EmployeesActions from '../../../settings/actions/employeeActions'
import { IEmployee } from '../../../settings/types/employee'
import * as TimeTrackingActions from '../../../time-tracking/actions/timeTrackingActions'
import TimeTrackingModal from '../../../time-tracking/components/TimeTrackingModal'
import { ITimeTracking } from '../../../time-tracking/types/timeTracking'
import * as CostsActions from '../../actions/directAndPassTroughCosts'
import ActivityDetailsLogs from '../../components/activity-details/ActivityDetailsLogs'
import ActivityDetailsTimesheet from '../../components/activity-details/ActivityDetailsTimesheet'
import DirectAndPassTroughDetailsOverviewComponent from '../../components/activity-details/CostDetailsOverview'
import { _initialDirectCost } from '../../reducers/directAndPassTroughCostsReducer'
import { ICostHistory, IDirectCost, IPassThroughCost } from '../../types/directAndPassTroughCosts'
import { IProject } from '../../types/project'
import { IQueryActivityEmployee, TaskStatus } from '../../types/projectActivity'

const ConfirmModal = React.lazy(() => import('../../../../components/Modal/ConfirmDeleteModal'))

interface StateProps {
  selectedCost: IDirectCost
  directCosts: IDirectCost[]
  project: IProject
  employees: IEmployee[]
  timeTrackingEntries: ITimeTracking[]
  openCostDetailsModal: boolean
  user: IUserDetail | null
  history: ICostHistory[]
  employee: IEmployee | undefined
  ptcs: IPassThroughCost[]
}

interface DispatchProps {
  updateDirectCost: (projectId: number | string, cost: IDirectCost, changed: string) => Promise<IDirectCost>
  getDirectCost: (projectId: number | string, cost: IDirectCost) => Promise<IDirectCost>
  setSelectedCost: (cost: IDirectCost) => void
  getEmployees: (queryParams?: IQueryActivityEmployee) => void
  getTimeTracking: (queryParams: Record<string, string>) => void
  deleteTimeTrackingEntry: (entry: ITimeTracking) => Promise<ITimeTracking>
  updateTimeTrackingEntry: (entry: ITimeTracking) => Promise<ITimeTracking>
  getDirectCostHistory: (projectId: number | string, cost: IDirectCost, searchQuery?: string) => void
  setOpenDetailsModal: (open: boolean) => void
  setActiveBudgetTab: (tab: string) => void
  getDCFilters: (id: string | number) => void
}

type Props = StateProps & DispatchProps

const DirectAndPassTroughDetailsContainer = (props: Props): JSX.Element => {
  const {
    project,
    selectedCost,
    directCosts,
    employees,
    timeTrackingEntries,
    openCostDetailsModal,
    user,
    history,
    employee,
    ptcs,
    updateDirectCost,
    getDirectCost,
    setSelectedCost,
    getEmployees,
    getTimeTracking,
    getDirectCostHistory,
    deleteTimeTrackingEntry,
    updateTimeTrackingEntry,
    setOpenDetailsModal,
    setActiveBudgetTab,
    getDCFilters
  } = props

  const _tabs = [
    { name: 'Overview', key: 'overview', rights: [] },
    { name: 'Timesheet', key: 'timesheet', rights: [15, 22] },
    { name: 'Logs', key: 'logs', rights: [] }
  ]
  const [activeTab, setActiveTab] = useState(_tabs[0].key)
  const [costStatus, setCostStatus] = useState<IDirectCost['status']>(selectedCost.status)
  const [costName, setCostName] = useState('')
  const [_employees, setEmployees] = useState<IEmployee[]>([])
  const [openTTConfirmModal, setOpenTTConfirmModal] = useState(false)
  const [openTTModal, setOpenTTModal] = useState(false)
  const [selectedTTEntry, setSelectedTTEntry] = useState<ITimeTracking | undefined>(undefined)

  const windowHeight = useWindowSize().height

  useEffect(() => {
    if (selectedCost && selectedCost.id) {
      getTimeTracking({ direct_cost: selectedCost.id.toString(), module: 'task_sidebar' })
    }
    if (project.permissions === 'restricted') {
      getEmployees({ projects: project.id.toString() })
    } else {
      getEmployees()
    }
  }, [getEmployees, getTimeTracking, project, selectedCost])

  useEffect(() => {
    if (project.time_tracking_perm.task_permission === 'all_prj_tasks') {
      const empIds = timeTrackingEntries.flatMap(e => +e.employee.id)
      const _emps = employees.filter(e => empIds.includes(+e.id))
      setEmployees(_emps.length > 0 ? _emps : [])
    } else {
      setEmployees(employees)
    }
  }, [employees, project, timeTrackingEntries, selectedCost])

  useEffect(() => {
    setCostName(selectedCost.offer_position)
    setCostStatus(selectedCost.status)
  }, [selectedCost])

  const handleTabClick = (data: MenuItemProps) => {
    if (data.name) {
      setActiveTab(data.name.toLowerCase())
    }
  }

  const updateCost = (projectId: number | string, cost_update: IDirectCost, openDatepicker: boolean) => {
    if (!openDatepicker) {
      let changed = ''
      if (selectedCost.status !== cost_update.status) {
        changed = cost_update.status
      }
      updateDirectCost(projectId, cost_update, changed).then(() => {
        getDirectCostHistory(project.id, selectedCost as IDirectCost)
        getDirectCost(projectId, cost_update).then(
          resp => {
            setSelectedCost(resp)
            setCostName(resp.offer_position)
            setCostStatus(resp.status)
          })
      })
    }
  }

  const inputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    setCostName(event.target.value)
  }

  const inputOnBlur = (event: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLTextAreaElement>) => {
    const value = event.target.value
    if (value) {
      updateCost(project.id, { ...selectedCost, offer_position: value }, false)
      setCostName(value)
    }
  }

  const selectTimeTrackingEntry = (entry: ITimeTracking) => {
    setSelectedTTEntry({...entry, direct_cost: selectedCost})
  }

  const openTimeTrackingModal = (open: boolean) => {
    setOpenTTModal(open)
  }

  const openTTConfirmDeleteModal = (open: boolean) => {
    setOpenTTConfirmModal(open)
  }

  const ttInput = (action: 'cancel' | 'delete') => {
    if (action === 'delete' && selectedTTEntry) {
      deleteTimeTrackingEntry(selectedTTEntry).then(() => {
        getDCFilters(project.id)
        if (selectedCost.id !== '') {
          getTimeTracking({ direct_cost: selectedCost.id.toString(), module: 'task_sidebar' })
          if (selectedCost.project_phase) {
            getDirectCost(project.id, selectedCost)
          }
        }
      })
    }
    setOpenTTConfirmModal(false)
  }

  const saveEntry = (entry: ITimeTracking) => {
    const query = selectedCost.project_phase ? 'direct_cost' : 'pass_trough_cost'
    updateTimeTrackingEntry(entry).then(() => {
      getTimeTracking({ [query]: selectedCost.id.toString(), module: 'task_sidebar' })
      getDirectCost(project.id, selectedCost)
      getDCFilters(project.id)
    })
  }

  const renderHeader = () => {
    return (
      <Header style={{ marginBottom: 0, display: 'flex', justifyContent: 'space-between' }}>
        <ValidationInput
          fluid
          placeholder='Enter task name'
          fieldWidthPercentage={'100%'}
          className={'act-name-input'}
          // Display only (the field is disabled): show the key merged into the name as
          // everywhere else (#89). costName itself stays the raw name, so nothing can
          // persist the merged string.
          value={taskDisplayName(selectedCost.key, costName)}
          onChange={e => inputChange(e)}
          onBlur={e => inputOnBlur(e)}
          symbolPadding={.3}
          transparent={true}
          disabled={true}
        />
      </Header>
    )
  }

  const filterHistory = (searchQuery: string) => {
    getDirectCostHistory(project.id, selectedCost as IDirectCost, searchQuery)
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <DirectAndPassTroughDetailsOverviewComponent
            cost={selectedCost}
            costStatus={costStatus}
            project={project}
            employees={_employees}
            updateCost={updateCost}
            costName={costName}
            user={user}
            setActiveBudgetTab={setActiveBudgetTab}
            onCloseModal={closeModal}
          />
        )

      case 'timesheet':
        return (
          <ActivityDetailsTimesheet
            selectedActivity={selectedCost}
            timeTrackingEntries={timeTrackingEntries}
            getTimeTracking={getTimeTracking}
            selectEntry={selectTimeTrackingEntry}
            openModal={openTimeTrackingModal}
            openConfirmModal={openTTConfirmDeleteModal}
            archived={project.active}
            query={selectedCost.project_phase ? 'direct_cost' : 'pass_trough_cost'}
            user={user}
          />
        )

      case 'logs':
        return <ActivityDetailsLogs history={history ? history : []} filterHistory={filterHistory} />

      default:
        return null
    }
  }

  const renderTabs = () => {
    return (
      <Menu widths={3} pointing secondary style={{ margin: '32px 0 16px', justifyContent: 'start' }}>
        {_tabs.map(tab => {
          if (user && checkRole(user, tab.rights)) {
            return (
              <Menu.Item
                style={{
                  color: activeTab === tab.key ? '#08D6A1' : '#172533',
                  fontWeight: activeTab === tab.key ? 'bold' : '#172533',
                  fontSize: '18px',
                  letterSpacing: '0',
                  lineHeight: '21px',
                  paddingTop: '0',
                  paddingBottom: '18px'
                }}
                key={tab.name}
                name={tab.name}
                active={activeTab === tab.key}
                onClick={(e, data) => handleTabClick(data)}
              />
            )
          }
        })}
      </Menu>
    )
  }

  const handleMarkButton = () => {
    const status: TaskStatus[0] = costStatus === 'completed' ? +selectedCost.current_hours > 0 ? 'in_progress' : 'upcoming' : 'completed'
    updateCost(project.id, { ...selectedCost, status: status }, false)
  }

  const renderButton = () => {
    const isCompleted = costStatus === 'completed'
    if (activeTab === 'overview'
      && project.active
      && user
      && checkRole(user, [15])
      && checkTeamMemberOrAdminStatus(user, project)) {
      return (
        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#172533', fontSize: '20.25px', fontWeight: 'bold' }}>Task Detail</div>
          <Button
            size='small'
            primary={true}
            content={isCompleted ? 'Mark as undone' : 'Mark as completed'}
            style={{ fontSize: '14px', borderRadius: '8px', width: '145px', height: '28px', padding: 0 }}
            onClick={handleMarkButton}
          />
        </div>
      )
    }
    return null
  }

  const closeModal = () => {
    if (!openTTModal && !openTTConfirmModal) {
      setSelectedCost(_initialDirectCost)
      setOpenDetailsModal(false)
    }
  }

  const renderContent = () => {
    return (
      <div style={{
        position: 'fixed',
        height: windowHeight,
        width: '39rem',
        background: '#FFFFFF',
        top: 0,
        right: 0,
        boxShadow: '0 0 10px -5px #151515',
        borderRadius: '8px',
        zIndex: 999
      }}>
        <Image id='closeModal' src={close_modal} onClick={closeModal} style={{ margin: '6%', cursor: 'pointer' }} />
        <div style={{ display: 'flex', flexDirection: 'column', margin: '0 32px 0' }}>
          {renderHeader()}
          {renderTabs()}
          {renderButton()}
        </div>
        {renderTabContent()}
      </div>
    )
  }

  const renderTTModal = () => {
    // TODO -- projects={projectsByPermission} ???
    if (employee && openTTModal) {
      const query = selectedCost.project_phase ? 'direct_costs' : 'pass_trough_cost'
      const query_data = selectedCost.project_phase ? directCosts : ptcs
      return (
        <TimeTrackingModal
          entry={selectedTTEntry}
          projects={[{ ...project, [query]: query_data }]}
          employee={employee}
          date={selectedTTEntry ? new Date(selectedTTEntry.date) : undefined}
          open={openTTModal}
          saveTimeTrackingEntry={saveEntry}
          closeModal={openTimeTrackingModal}
        />
      )
    }
    return null
  }

  return (
    <React.Fragment>
      <TransitionModal
        visible={openCostDetailsModal}
        animation='slide left'
        duration={{ show: 500, hide: 500 }}
        children={renderContent()}
        clickOutside={closeModal}
      />
      {renderTTModal()}
      <ConfirmModal open={openTTConfirmModal} userInput={ttInput} />
    </React.Fragment>
  )
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    selectedCost: state.projectsState.selectedCost as IDirectCost,
    directCosts: state.projectsState.directCosts,
    project: state.projectsState.project,
    employees: state.userSettingsState.employees,
    timeTrackingEntries: state.timeTrackingState.timeTrackingEntries,
    openCostDetailsModal: state.projectsState.openCostDetailsModal,
    employee: state.authState.user?.employee,
    history: state.projectsState.costHistory,
    user: state.authState.user,
    ptcs: state.projectsState.passTroughCosts
  }
}

const mapDispatchToProps = (
  dispatch: CostsActions.DirectAndPassTroughCostThunkDispatchType & EmployeesActions.EmployeeThunkDispatchType & TimeTrackingActions.TimeTrackingThunkDispatchType
): DispatchProps => {
  return {
    updateDirectCost: async (projectId, cost, changed) => dispatch(CostsActions.updateDirectCost(projectId, cost, changed)),
    getDirectCost: (projectId, cost) => dispatch(CostsActions.getDirectCost(projectId, cost)),
    setSelectedCost: cost => dispatch(CostsActions.setSelectedCost(cost)),
    getEmployees: (queryParams?) => dispatch(EmployeesActions.getEmployees(queryParams)),
    getTimeTracking: queryParams => dispatch(TimeTrackingActions.getTimeTrackingEntries(queryParams)),
    deleteTimeTrackingEntry: entry => dispatch(TimeTrackingActions.deleteTimeTracking(entry)),
    getDirectCostHistory: (projectId, cost, search) => dispatch(CostsActions.getDirectCostHistory(projectId, cost, search)),
    updateTimeTrackingEntry: entry => dispatch(TimeTrackingActions.updateTimeTracking(entry)),
    setOpenDetailsModal: (open) => dispatch(CostsActions.setOpenCostDetailsModal(open)),
    setActiveBudgetTab: (tab) => dispatch(CostsActions.setActiveSPTBudgetTab(tab)),
    getDCFilters: id => dispatch(CostsActions.getCostFilters(id)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withSuspense(DirectAndPassTroughDetailsContainer))
